import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useReminderSettings } from "@/features/business/hooks/reminderSettingsQueries";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { CustomerEmailEventsSection } from "../components/CustomerEmailEventsSection";
import { ReminderSettingsSection } from "../components/ReminderSettingsSection";

function ReminderSettingsLoader({ businessId }: { businessId: string }) {
  const query = useReminderSettings(businessId);

  if (!query.data) {
    return query.error ? (
      <ErrorState title="Couldn't load reminders" message={query.error.message} onRetry={() => void query.refetch()} />
    ) : <LoadingSpinner message="Loading reminder settings..." />;
  }

  return (
    <ReminderSettingsSection
      businessId={businessId}
      settings={query.data}
      refreshError={query.error?.message}
      onRetry={() => void query.refetch()}
      onReload={async () => {
        const result = await query.refetch();
        if (result.error) throw result.error;
        if (!result.data) throw new Error("No reminder settings were returned.");
        return result.data;
      }}
    />
  );
}

export function CustomerEmailsPage() {
  const { user } = useAuth();
  const { business, businessId, loading } = useCurrentBusiness();

  return (
    <Page>
      <PageHeader title="Reminders & emails" description="Manage outgoing customer emails for your business." />
      <PageContent>
        {loading ? <LoadingSpinner message="Loading business..." /> : !business || !businessId ? (
          <ErrorState message="Select a business to manage customer emails." />
        ) : business.ownerId !== user?.id ? (
          <ErrorState message="Only the business owner can manage customer email settings." />
        ) : (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              These settings apply to all locations of {business.name}, not just the selected location.
            </p>
            <ReminderSettingsLoader key={businessId} businessId={businessId} />
            <CustomerEmailEventsSection />
          </div>
        )}
      </PageContent>
    </Page>
  );
}
